import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Asset } from "@/types/schema";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "@/components/ui/use-toast";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  assets: Asset[];
  onSave: (assets: Asset[]) => void;
}

export default function AssetManagementModal({ open, onOpenChange, assets, onSave }: Props) {
  const [editedAssets, setEditedAssets] = useState<Asset[]>([...assets]);

  const handleAddAsset = () => {
    const newAsset: Asset = {
      assetId: `A${Date.now()}`,
      description: "New Asset",
      basePrice: 0,
      hsn: "",
      gstRate: 18,
      quantity: 1,
      serialNo: "",
      poNumber: ""
    };
    setEditedAssets([...editedAssets, newAsset]);
  };

  const handleRemoveAsset = (index: number) => {
    const updated = editedAssets.filter((_, i) => i !== index);
    setEditedAssets(updated);
  };

  const handleFieldChange = (index: number, field: keyof Asset, value: any) => {
    const updated = [...editedAssets];
    updated[index] = { ...updated[index], [field]: value };
    setEditedAssets(updated);
  };

  const handleSave = () => {
    // Validate that all assets have required fields
    const invalid = editedAssets.some(a => !a.description || !a.hsn || a.basePrice <= 0);
    if (invalid) {
      toast({
        title: "Validation Error",
        description: "All assets must have description, HSN, and base price > 0",
        variant: "destructive"
      });
      return;
    }

    onSave(editedAssets);
    toast({
      title: "Assets Updated",
      description: "Asset values have been updated successfully.",
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Assets</DialogTitle>
          <DialogDescription>
            Add or modify assets to test different calculation scenarios
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={handleAddAsset} size="sm" className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Asset
            </Button>
          </div>

          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[250px]">Description</TableHead>
                  <TableHead className="w-[120px]">HSN</TableHead>
                  <TableHead className="w-[120px]">Base Price</TableHead>
                  <TableHead className="w-[100px]">Quantity</TableHead>
                  <TableHead className="w-[100px]">GST Rate</TableHead>
                  <TableHead className="w-[120px]">Serial No</TableHead>
                  <TableHead className="w-[120px]">PO Number</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {editedAssets.map((asset, index) => (
                  <TableRow key={asset.assetId}>
                    <TableCell>
                      <Input
                        value={asset.description}
                        onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                        placeholder="Asset description"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={asset.hsn}
                        onChange={(e) => handleFieldChange(index, 'hsn', e.target.value)}
                        placeholder="HSN code"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={asset.basePrice}
                        onChange={(e) => handleFieldChange(index, 'basePrice', parseFloat(e.target.value) || 0)}
                        placeholder="0"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={asset.quantity || 1}
                        onChange={(e) => handleFieldChange(index, 'quantity', parseInt(e.target.value) || 1)}
                        placeholder="1"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={asset.gstRate.toString()}
                        onValueChange={(value) => handleFieldChange(index, 'gstRate', parseInt(value) as 12 | 18 | 28)}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="12">12%</SelectItem>
                          <SelectItem value="18">18%</SelectItem>
                          <SelectItem value="28">28%</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={asset.serialNo || ""}
                        onChange={(e) => handleFieldChange(index, 'serialNo', e.target.value)}
                        placeholder="Serial no"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={asset.poNumber || ""}
                        onChange={(e) => handleFieldChange(index, 'poNumber', e.target.value)}
                        placeholder="PO number"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAsset(index)}
                        disabled={editedAssets.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {editedAssets.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No assets added. Click "Add Asset" to get started.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
